// =============================================
// Humanization Service
// v3.0: 4 tiers (Critical/High/Medium/Polish) + Writing Profile
// =============================================

import { supabase } from "@/integrations/supabase/client";
import { getLLMProvider, getStageConfig } from "@/services/llm";
import { loadPrompt, fillTemplate } from "@/services/llm/promptLoader";
import type { ThematicWritingProfile } from "@/types/thematicMaps";
import { logPublic, logDebug } from "./contentLogger";
import { protectLinks, restoreLinks, withLinkMarkerNote } from "./linkWeaver";
import { cleanupEmDashes } from "./emDashCleanup";

// =============================================
// Hybrid Data Protection (shared with optimization)
// =============================================

const SOURCE_RE = /\((?:Source|Źródło|Quelle):\s*(?:[^()]*|\([^()]*\))*\)/g;
const NUM_RE = /\b\d[\d,.\-–]*\s*(?:%|procent|mln|mld|tys|kg|g|mg|μg|m|cm|km|l|ml|ha|kcal|cal|kWh|Hz|MHz|GHz|zł|PLN|USD|EUR|\$|€)\b/g;
const DATE_RE = /\b(?:19|20)\d{2}(?:\s*[-–]\s*(?:19|20)\d{2})?\b/g;

interface ProtectionMap {
  placeholders: Map<string, string>;
  spans: Map<string, string>;
}

function protectData(html: string): { protectedHtml: string; map: ProtectionMap } {
  const map: ProtectionMap = { placeholders: new Map(), spans: new Map() };
  let result = html;

  result = result.replace(SOURCE_RE, (match) => {
    const id = `[[SRC_${String(map.placeholders.size + 1).padStart(3, "0")}]]`;
    map.placeholders.set(id, match);
    return id;
  });

  result = result.replace(NUM_RE, (match) => {
    const id = `NUM_${Math.random().toString(36).slice(2, 8)}`;
    map.spans.set(id, match);
    return `<span data-token-id="${id}">${match}</span>`;
  });

  result = result.replace(DATE_RE, (match) => {
    const id = `DAT_${Math.random().toString(36).slice(2, 8)}`;
    map.spans.set(id, match);
    return `<span data-token-id="${id}">${match}</span>`;
  });

  return { protectedHtml: result, map };
}

function restoreData(html: string, map: ProtectionMap): string {
  let result = html;
  result = result.replace(/<span data-token-id="[^"]*">([\s\S]*?)<\/span>/g, "$1");
  for (const [placeholder, original] of map.placeholders) {
    result = result.replaceAll(placeholder, original);
  }
  return result;
}

// =============================================
// Banned phrases (AI patterns)
// =============================================

const BANNED_AI_PHRASES_PL = [
  "Warto zauważyć", "Podsumowując", "W dzisiejszych czasach",
  "Nie da się ukryć", "Kluczowe jest to, że", "Oto kilka",
  "Poniżej przedstawiamy", "Zacznijmy od", "Warto wspomnieć",
  "Jak widać", "Nie ulega wątpliwości", "Podsumowanie",
  "W tym artykule", "Przyjrzyjmy się", "Warto podkreślić",
];

const BANNED_AI_PHRASES_EN = [
  "It's worth noting", "In summary", "These days",
  "It can't be hidden", "The key is that", "Here are a few",
  "Below we present", "Let's start with", "It's important to mention",
  "As we can see", "There's no doubt", "In this article",
  "Let's take a look", "It's worth emphasizing", "In conclusion",
];

// =============================================
// Writing Profile → humanization rules
// =============================================

function buildHumanizationRules(profile: ThematicWritingProfile | null, language: string): string {
  const parts: string[] = [];

  // TIER 1: Critical
  parts.push(`## TIER 1 - KRYTYCZNE (obowiązkowe)
- Mieszaj zdania krótkie (5-8 słów) z długimi (15-25 słów)
- USUŃ wszystkie AI patterns: ${language === "pl" ? BANNED_AI_PHRASES_PL.join(", ") : BANNED_AI_PHRASES_EN.join(", ")}`);

  // TIER 2: High
  parts.push(`\n## TIER 2 - WYSOKIE`);
  if (profile) {
    if (profile.voice_pronouns === "ty") {
      parts.push(`- Używaj "ty", "twój" — styl nieformalny, bezpośredni`);
    } else if (profile.voice_pronouns === "wy") {
      parts.push(`- Używaj "wy", "wasz" — styl nieformalny plural`);
    } else if (profile.voice_pronouns === "pan_pani") {
      parts.push(`- Używaj "Pan/Pani" — styl formalny`);
    } else {
      parts.push(`- Styl bezosobowy — unikaj zwracania się bezpośrednio`);
    }

    if (profile.claim_strength === "strong") {
      parts.push(`- Bezpośrednie stwierdzenia dozwolone ("to jest", "to działa")`);
    } else if (profile.claim_strength === "cautious") {
      parts.push(`- Używaj hedgingu: "wydaje się", "prawdopodobnie", "badania sugerują"`);
    } else {
      parts.push(`- Umiarkowany hedging: "wskazuje na to", "w wielu przypadkach"`);
    }

    if (profile.preferred_phrases?.length > 0) {
      parts.push(`- WPLEĆ naturalnie te zwroty: ${profile.preferred_phrases.join(", ")}`);
    }
  }
  parts.push(`- Unikaj przewidywalnych synonimów — użyj zróżnicowanego słownictwa`);

  // TIER 3: Medium
  parts.push(`\n## TIER 3 - SREDNIE`);
  parts.push(`- Żadne dwa kolejne zdania NIE zaczynają się tak samo`);
  parts.push(`- Mieszaj długość akapitów: 1-2 zdania + 4-5 zdań`);

  if (profile?.analogy_frequency !== "none" && profile?.analogy_frequency) {
    parts.push(`- Użyj ${profile.analogy_frequency === "frequent" ? "3-4" : profile.analogy_frequency === "moderate" ? "1-2" : "0-1"} analogii${profile.analogy_domain ? ` z domeny: ${profile.analogy_domain}` : ""}`);
  }

  // TIER 4: Polish
  parts.push(`\n## TIER 4 - POLEROWANIE (QA końcowe)`);
  parts.push(`- Płynne przejścia między zdaniami i akapitami`);
  parts.push(`- Logiczna progresja myśli`);

  if (profile?.banned_phrases?.length) {
    parts.push(`- HARD FAIL jeśli znajdziesz: ${profile.banned_phrases.join(", ")}`);
  }

  if (profile?.language_rules) {
    parts.push(`- Reguły językowe: ${profile.language_rules}`);
  }

  return parts.join("\n");
}

// =============================================
// Main humanization function
// =============================================

export interface HumanizationResult {
  finalHtml: string;
  costUsd: number;
  warnings: string[];
  report: {
    tiersApplied: number;
    aiPhrasesRemoved: number;
    bannedPhrasesFound: number;
  };
}

/**
 * Apply 4-tier humanization with Hybrid Data Protection + Writing Profile.
 */
export async function humanizeArticle(
  optimizedHtml: string,
  mapId: string,
  nodeId: string,
  language: string,
): Promise<HumanizationResult> {
  // 1. Load Writing Profile
  const { data: map } = await (supabase as any)
    .from("thematic_maps")
    .select("active_profile_id")
    .eq("id", mapId)
    .single();

  let profile: ThematicWritingProfile | null = null;
  if (map?.active_profile_id) {
    const { data } = await (supabase as any)
      .from("thematic_writing_profiles")
      .select("*")
      .eq("id", map.active_profile_id)
      .single();
    profile = data;
  }

  // 2. Apply Hybrid Data Protection
  // OSLONA LINKOW [[LNK_nnn]] (2026-08-23) — jak w optimizationService.
  const { html: linkProtectedHtml, map: linkMap } = protectLinks(optimizedHtml);
  const { protectedHtml, map: protectionMap } = protectData(linkProtectedHtml);

  // 3. Build humanization rules from Writing Profile
  const rules = buildHumanizationRules(profile, language);

  // 4. Load prompt from DB (user-editable in Settings → Prompty AI)
  const dbPrompt = await loadPrompt("humanization");
  const stageConfig = getStageConfig("humanization");

  // Build profile enhancement section (if profile exists)
  let profileSection = "";
  if (profile) {
    const voiceChars = ((profile as any).voice_characteristics || []).join("; ");
    const contentPers = (profile as any).content_personality || "";
    profileSection = "\n\n### AUTHOR WRITING PROFILE\nMaintain the author's perspective and formality level. Do NOT insert any specific phrases - humanization rules take priority over style preferences.\n- Author: " + (profile.public_author_label || profile.author_identity || "expert") + "\n- Voice: " + (profile.voice_pronouns || "standard") + "\n- Claim strength: " + (profile.claim_strength || "moderate") + "\n- Language rules: " + (profile.language_rules || "") + (voiceChars ? "\n- Voice characteristics: " + voiceChars : "") + (contentPers ? "\n- Content personality: " + contentPers : "") + "\n\nRULES:\n- ENHANCE the existing author voice, do NOT create a new persona\n- The text already has a style - your job is to polish, not rewrite from scratch\n- If the text already sounds natural, change MINIMUM\n- Do NOT insert repetitive phrases - vary language naturally";
    logPublic("HUMAN", "Profil: " + (profile.public_author_label || profile.author_identity || "(unnamed)"));
  }

  const templateVars: Record<string, string> = {
    rules,
    author_identity: profile?.author_identity || "doświadczonego eksperta",
    public_author_label: profile?.public_author_label || "",
    voice_pronouns: profile?.voice_pronouns || "",
    voice_characteristics: ((profile as any)?.voice_characteristics || []).join("; "),
    content_personality: (profile as any)?.content_personality || "",
    claim_strength: profile?.claim_strength || "",
    language_rules: profile?.language_rules || "",
    language,
    article_html: protectedHtml,
  };

  // Single-pass humanization (all 4 tiers in one LLM call)
  const authorLabel = profile?.author_identity || "doswiadczonego eksperta";
  const fallbackPrompt = [
    "Jestes humanizatorem tresci. Zastosuj 4 warstwy humanizacji do artykulu.",
    "",
    rules,
    "",
    "## WAZNE ZASADY",
    "- NIE modyfikuj danych w <span data-token-id=\"...\"> ani [[SRC_*]] placeholderow",
    "- NIE dodawaj nowych linkow <a>",
    "- NIE usuwaj naglowkow H1, H2, H3",
    "- NIE zmieniaj liczb, dat, zrodel",
    "- Zachowaj dokladnie te sama strukture HTML (te same tagi)",
    "- Artykul powinien brzmiec jak napisany przez " + authorLabel,
    "- Jezyk: " + language,
    "- Zwroc TYLKO HTML",
    "",
    "## Artykul do humanizacji",
    protectedHtml,
  ].join("\n");

  const rawPrompt = dbPrompt
    ? fillTemplate(dbPrompt.template, templateVars)
    : fallbackPrompt;

  // Append profile enhancement section to prompt (if profile exists)
  const prompt = withLinkMarkerNote(
    profileSection ? rawPrompt + profileSection : rawPrompt,
    protectedHtml,
  );

  const humanProvider = getLLMProvider(dbPrompt?.provider || stageConfig.provider);
  const response = await humanProvider.call(prompt, {
    model: dbPrompt?.modelId || stageConfig.model,

    temperature: dbPrompt?.temperature ?? 0.6,
    reasoningEffort: "medium",
    step: "humanization", mapId, nodeId,
  });

  // 5. Restore protected data + clean DOCTYPE/html wrapper if model added it
  const rawOutput = response.text.trim()
    .replace(/^[\s\S]*?(<h1)/i, "$1")
    .replace(/<\/body>[\s\S]*$/i, "")
    .replace(/<\/html>[\s\S]*$/i, "")
    .trim();
  let finalHtml = restoreData(rawOutput, protectionMap);
  const linkRestore = restoreLinks(finalHtml, linkMap);
  finalHtml = linkRestore.html;

  // 5b. Em-dash post-processing: max 4/1000 slow — TYLKO interpunkcja zdaniowa.
  // Zakresy liczbowe (8–12, 200–450 zl) sa NIETYKALNE: stary blok lapal tu
  // polpauze miedzy cyframi i produkowal "8, 12 godzin" (root cause 2026-08-24,
  // szczegoly w emDashCleanup.ts).
  const dashCleanup = cleanupEmDashes(finalHtml);
  if (dashCleanup.converted > 0) {
    finalHtml = dashCleanup.html;
    logPublic(
      "HUMAN",
      `Em-dash cleanup: ${dashCleanup.converted} interpunkcyjnych -> przecinki (limit ${dashCleanup.limit}, zakresy liczbowe nietkniete)`,
    );
  }

  // 6. Count removed AI phrases
  const bannedPhrases = language === "pl" ? BANNED_AI_PHRASES_PL : BANNED_AI_PHRASES_EN;
  const aiPhrasesInInput = bannedPhrases.filter((p) =>
    optimizedHtml.toLowerCase().includes(p.toLowerCase()),
  ).length;
  const aiPhrasesInOutput = bannedPhrases.filter((p) =>
    finalHtml.toLowerCase().includes(p.toLowerCase()),
  ).length;

  // 7. Check banned phrases from profile
  const profileBanned = profile?.banned_phrases || [];
  const bannedFound = profileBanned.filter((p) =>
    finalHtml.toLowerCase().includes(p.toLowerCase()),
  );

  const warnings: string[] = [...linkRestore.warnings];
  if (bannedFound.length > 0) {
    warnings.push("Banned phrases still present: " + bannedFound.join(", "));
  }

  // 7b. Phrase repetition check (4-word phrases appearing 3+ times)
  const textOnly = finalHtml.replace(/<[^>]+>/g, " ").toLowerCase();
  const phWords = textOnly.split(/\s+/).filter(w => w.length > 2);
  const phraseCounts = new Map<string, number>();
  for (let pi = 0; pi < phWords.length - 3; pi++) {
    const phrase = phWords.slice(pi, pi + 4).join(" ");
    phraseCounts.set(phrase, (phraseCounts.get(phrase) || 0) + 1);
  }
  for (const [phrase, count] of phraseCounts) {
    if (count >= 3) {
      logDebug("HUMAN", `WARN: repeated phrase "${phrase}" (${count}x)`);
    }
  }

  // 8. Save to DB
  await (supabase as any)
    .from("thematic_node_content")
    .update({
      final_html: finalHtml,
      engine_stage: "ready",
      engine_completed_at: new Date().toISOString(),
      content_status: "content_ready",
    })
    .eq("node_id", nodeId);

  return {
    finalHtml: finalHtml,
    costUsd: response.costUsd || 0,
    warnings,
    report: {
      tiersApplied: 4,
      aiPhrasesRemoved: aiPhrasesInInput - aiPhrasesInOutput,
      bannedPhrasesFound: bannedFound.length,
    },
  };
}
